import type {
  IOptions,
  IOptionsProps,
  IUseFilterProps,
} from "@fluidattacks/design";
import { useFilters } from "@fluidattacks/design";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import type { IExploitResult } from "../types";
import { useFiltersStorage } from "storage/filters";

const useExploitResultsFilters = (
  dataset: IExploitResult[],
  setFilteredDataset: React.Dispatch<React.SetStateAction<IExploitResult[]>>,
): IUseFilterProps<IExploitResult> => {
  const { t } = useTranslation();
  const { setValues, getValues } = useFiltersStorage();

  const uniqueStates = useMemo(
    (): string[] =>
      Array.from(new Set(dataset.map((item): string => item.state))),
    [dataset],
  );

  const uniqueKinds = useMemo(
    (): string[] =>
      Array.from(new Set(dataset.map((item): string => item.kind ?? ""))),
    [dataset],
  );

  const uniqueWhos = useMemo(
    (): string[] =>
      Array.from(new Set(dataset.map((item): string => item.who ?? ""))),
    [dataset],
  );

  const uniqueWheres = useMemo(
    (): string[] =>
      Array.from(new Set(dataset.map((item): string => item.where ?? ""))),
    [dataset],
  );

  const filterOptions: IOptionsProps<IExploitResult>[] = useMemo(
    (): IOptionsProps<IExploitResult>[] => [
      {
        filterOptions: [
          {
            key: "exploitability",
            label: t("group.forces.compromisedToe.exploitability"),
            type: "text",
          },
        ],
        label: t("group.forces.compromisedToe.exploitability"),
      },
      {
        filterOptions: [
          {
            key: "state",
            label: t("group.forces.compromisedToe.status"),
            options: uniqueStates.map(
              (value): IOptions => ({ label: value, value }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.forces.compromisedToe.status"),
      },
      {
        filterOptions: [
          {
            key: "kind",
            label: t("group.forces.compromisedToe.type"),
            options: uniqueKinds.map(
              (value): IOptions => ({ label: value, value }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.forces.compromisedToe.type"),
      },
      {
        filterOptions: [
          {
            key: "who",
            label: t("group.forces.compromisedToe.specific"),
            options: uniqueWhos.map(
              (value): IOptions => ({ label: value, value }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.forces.compromisedToe.specific"),
      },
      {
        filterOptions: [
          {
            key: "where",
            label: t("group.forces.compromisedToe.where"),
            options: uniqueWheres.map(
              (value): IOptions => ({ label: value, value }),
            ),
            type: "checkboxes",
          },
        ],
        label: t("group.forces.compromisedToe.where"),
      },
    ],
    [t, uniqueStates, uniqueKinds, uniqueWhos, uniqueWheres],
  );

  const handleGetValues = useCallback((): string | null => {
    return getValues("exploit-results") as string | null;
  }, [getValues]);

  const handleSetValues = useCallback(
    (value: string): void => {
      void setValues("exploit-results", value);
    },
    [setValues],
  );

  return useFilters({
    dataset,
    getValues: handleGetValues,
    options: filterOptions,
    setFilteredDataset,
    setValues: handleSetValues,
  });
};
export { useExploitResultsFilters };
